import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Col, Container, Row } from '../../../Shared/Grid'
import { useTranslation, withTranslation } from 'react-i18next'
import './index.scss'
import Icon from '../../../Shared/Icon'
import { getDistributionChannels } from '../../../../api'
import Loader from '../../../Shared/Loader'
import activityTrackingService from '../../../../services/activityTrackingService'
import PageHead from '../../../Shared/PageHead'
import SearchField from '../../../Shared/SearchField'

const distributionTypeLabelMap = {
  'All': 'DISTRIBUTION_CHANNEL_ALL',
  'Tour Operator': 'DISTRIBUTION_CHANNEL_TOUR_OPERATOR',
  'OTA': 'DISTRIBUTION_CHANNEL_OTA',
  'IT & CRS': 'DISTRIBUTION_CHANNEL_IT_AND_CRS',
  'Bed Bank & GDS': 'DISTRIBUTION_CHANNEL_BED_BANK_AND_GDS',
  'Metasearch': 'DISTRIBUTION_CHANNEL_METASEARCH',
  'Travel Agency': 'DISTRIBUTION_CHANNEL_TRAVEL_AGENCY'
}

const updateFreqPriority = {
  'Quarterly': 1,
  'Bi-monthly': 2,
  'Monthly': 3,
  'Bi-Weekly': 4,
  'Weekly': 5,
  'Daily': 6,
  'Twice daily': 7,
  'Every 6 hours': 8,
  'Real time': 9,
}

const statusPriority = {
  'Coming soon': 1,
  'Planned': 2,
  'Live': 3,
}

const SortableHeader = memo(({ title, onSort, field, currentField, currentSortDir }) => {
  const className = `icon${currentField === field ? ` ${currentSortDir}` : ''}`
  return (
    <td className="sortable" onClick={() => onSort(field)}>
      {title}
      {<span className={className}/>}
    </td>
  )
})

const ChannelList = () => {
  const [selectedDistributionType, setSelectedDistributionType] = useState('All')
  const [channels, setChannels] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredChannels, setFilteredChannels] = useState([])
  const channelListScrollableDiv = useRef()
  const [sortDir, setSortDir] = useState('desc')
  const [sortField, setSortField] = useState('updateCycle')
  const { t } = useTranslation()
  useEffect(() => {
    activityTrackingService.trackPageView('Channel List', {}, false)
    getDistributionChannels().then((data) => {
      setLoading(false)
      setChannels(data.records ?? [])
    }).catch(() => {
      setLoading(false)
    })
  }, [])
  useEffect(() => {
    setFilteredChannels(
      channels.filter((channel) => {
        if (selectedDistributionType === 'All') {
          return channel.name.toLowerCase().includes(searchTerm.toLowerCase())
        }
        return channel.types.indexOf(selectedDistributionType) !== -1
          && channel.name.toLowerCase().includes(searchTerm.toLowerCase())
      }).sort((a, b) => {
        let fieldA
        let fieldB
        switch (sortField) {
          case 'name':
            fieldA = a[sortField].toLowerCase()
            fieldB = b[sortField].toLowerCase()
            break
          case 'types':
          case 'contentUsed':
            fieldA = a[sortField][0] ?? ''
            fieldB = b[sortField][0] ?? ''
            break
          case 'updateCycle':
            fieldA = updateFreqPriority[a[sortField]] ?? 0
            fieldB = updateFreqPriority[b[sortField]] ?? 0
            break
          case 'status':
            fieldA = statusPriority[a[sortField]] ?? 0
            fieldB = statusPriority[b[sortField]] ?? 0
            break
          default:
            return 0
        }
        // pushing empty values to bottom first
        // then performing actual compare
        if (!fieldA) {
          return !fieldB ? 0 : 1
        } else if (!fieldB) {
          return -1
        } else {
          const comparison = fieldA < fieldB ? -1 : fieldA > fieldB ? 1 : 0
          return sortDir === 'asc' ? comparison : -comparison
        }
      })
    )
  }, [channels, selectedDistributionType, searchTerm, sortField, sortDir])

  const triggerSort = useCallback((_sortField) => {
    if (_sortField === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    }
    setSortField(_sortField)
  }, [sortField, sortDir])

  const createChannelList = (channels) => {
    return channels.map((channel) => (
      <tr key={channel.name + channel.types.join()}>
        <td data-title={t('DISTRIBUTION_CHANNEL_TH_NAME')}>{channel.name}</td>
        <td data-title={t('DISTRIBUTION_CHANNEL_TH_TYPE')} style={{ color: '#999da7' }}>
          {
            selectedDistributionType === 'All' ?
              channel.types.join(', ') : selectedDistributionType
          }
        </td>
        <td data-title={t('DISTRIBUTION_CHANNEL_TH_CONTENT_USED')}>{channel.contentUsed.join(', ')}</td>
        <td data-title={t('DISTRIBUTION_CHANNEL_TH_UPDATE_CYCLE')}>{channel.updateCycle}</td>
        <td data-title={t('DISTRIBUTION_CHANNEL_TH_STATUS')}>
          <div style={{ display: 'inline-flex', verticalAlign: 'middle', alignItems: 'center' }}>
            {getIcon(channel)}
            <span>{channel.status}</span>
          </div>
        </td>
        <td data-title={t('DISTRIBUTION_CHANNEL_TH_LINK')}>
          <a href={channel.link} target={'_blank'} rel={'noreferrer'}>
            <span style={{ color: '#3983bd' }}>View</span>
          </a>
        </td>
      </tr>
    ))
  }

  const getIcon = (channel) => {
    if (channel.status === 'Live') {
      return <Icon size={'medium'} type={'sound-waves'}/>
    }
    if (channel.status === 'Planned') {
      return <Icon size={'medium'} type={'calendar'}/>
    }
    if (channel.status === 'Coming soon') {
      return <Icon size={'medium'} type={'clock'}/>
    }
  }

  const createDistributionTypeHeader = (label, header) => {
    const className = selectedDistributionType === header
      ? 'distribution-types-header selected'
      : 'distribution-types-header'
    return <div
      onClick={() => {
        if (channelListScrollableDiv.current) {
          channelListScrollableDiv.current.scrollTop = 0
        }
        setSelectedDistributionType(header)
      }}
      className={className}
    >
      {label}
    </div>
  }

  return <div className="page page-channel-list">
    <Container fluid gutterWidth={5}>
      <Row>
        <Col xs={12}>
          <PageHead
            title={t('DISTRIBUTION_CHANNEL_LIST')}
            subtitle="We make your content available to these channels. Each channel decides their content usage and update frequency, we provide this info where possible."
          />
        </Col>
        <Col xs={12}>
            <span style={{ lineHeight: 2 }}>
              GIATA shares your hotel information, including images, amenities and descriptions
              with a fast-growing number of German tour operators and thousands of their travel agency partners,
              as well as with international travel sites, OTAs, bed banks, GDSs, etc.
            </span>
        </Col>
      </Row>
      <Row style={{ marginTop: '51px' }}>
        <Col xs={8}/>
        <Col xs={4}>
          <SearchField
            placeholderText={t('SEARCH_CHANNEL')}
            onInput={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm('')}
          />
        </Col>
      </Row>
      <br/>
      <br/>
      <Row className="distribution-types-headers">
        {
          Object.entries(distributionTypeLabelMap).map(([header, label]) =>
            createDistributionTypeHeader(t(label), header)
          )
        }
      </Row>
      <Row style={{ marginTop: '42px', marginBottom: '24px' }}>
        <Col xs={12}>
            <span style={{ color: '#bebebe' }}>
              Results for {t(distributionTypeLabelMap[selectedDistributionType])}: {filteredChannels.length}
            </span>
        </Col>
      </Row>
    </Container>
    <Container fluid gutterWidth={5}>
      {loading && <Loader/>}
      <Row>
        <Col xs={12}>
          <div className="flex-table">
            <div style={{ position: 'relative' }} className="table-responsive-vertical">
              <table className="table table-hover table-mc-light-blue" style={{ marginBottom: 0 }}>
                <thead>
                <tr>
                  <SortableHeader
                    title={t('DISTRIBUTION_CHANNEL_TH_NAME')}
                    onSort={triggerSort}
                    currentField={sortField}
                    currentSortDir={sortDir}
                    field="name"
                  />
                  {
                    selectedDistributionType === 'All'
                      ? <SortableHeader
                        title={t('DISTRIBUTION_CHANNEL_TH_TYPE')}
                        onSort={triggerSort}
                        currentField={sortField}
                        currentSortDir={sortDir}
                        field="types"
                      />
                      : <td>{t('DISTRIBUTION_CHANNEL_TH_TYPE')}</td>
                  }
                  <SortableHeader
                    title={t('DISTRIBUTION_CHANNEL_TH_CONTENT_USED')}
                    onSort={triggerSort}
                    currentField={sortField}
                    currentSortDir={sortDir}
                    field="contentUsed"
                  />
                  <SortableHeader
                    title={t('DISTRIBUTION_CHANNEL_TH_UPDATE_CYCLE')}
                    onSort={triggerSort}
                    currentField={sortField}
                    currentSortDir={sortDir}
                    field="updateCycle"
                  />
                  <SortableHeader
                    title={t('DISTRIBUTION_CHANNEL_TH_STATUS')}
                    onSort={triggerSort}
                    currentField={sortField}
                    currentSortDir={sortDir}
                    field="status"
                  />
                  <td>{t('DISTRIBUTION_CHANNEL_TH_LINK')}</td>
                </tr>
                </thead>
              </table>
            </div>
          </div>
          <div className="flex-table">
            <div ref={channelListScrollableDiv}
                 style={{ position: 'relative', maxHeight: '600px', overflowY: 'scroll' }}
                 className="table-responsive-vertical">
              <table className="table table-hover table-mc-light-blue">
                <tbody>
                {createChannelList(filteredChannels)}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  </div>
}

export default withTranslation()(ChannelList)
